import { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Paper,
  Box,
  Table,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  IconButton,
  Collapse,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";


import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { formatValueTo2Decimal } from "../../utils/Common/index";


const PortfolioOverview = ({ metrics, holdings, sectors }) => {
  const [expanded, setExpanded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleToggleExpand = () => setExpanded(!expanded);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedHoldings = [...holdings].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;
    const aVal = a[key] ?? 0;
    const bVal = b[key] ?? 0;
    return direction === "asc" ? aVal - bVal : bVal - aVal;
  });

  const displayedHoldings = expanded ? sortedHoldings : sortedHoldings.slice(0, 10);

  return (
    <>
      <Typography variant="h5" sx={{ mb: 2 }}>
        📊 Portfolio Overview
      </Typography>

      {/* 
        Needs to be added
        ["Portfolio Alpha", formatValueTo2Decimal(metrics.weighted_beta]
        ["Portfolio Beta", formatValueTo2Decimal(metrics.profit_loss_percent)],
        ["Volatility (Annualized)", formatValueTo2Decimal(metrics.volatility)],
        ["Sharpe Ratio", formatValueTo2Decimal(metrics.sharpe_ratio)],
        ["Maximum Drawdown", formatValueTo2Decimal(metrics.max_drawdown)], */}
      <Grid container spacing={2}>
        {Object.keys(metrics).length > 0 &&
          [
            ["Total Investment", formatValueTo2Decimal(metrics.total_investment, "₹")],
            ["Current Value", formatValueTo2Decimal(metrics.current_value, "₹")],
            ["Profit/Loss", formatValueTo2Decimal(metrics.profit_loss, "₹")],
            ["Portfolio Beta", "NAN"],
            ["Portfolio Alpha", "NAN"],
            ["Sharpe Ratio", "NAN"],
            ["Volatility (Annualized)", "NAN"],
            ["Maximum Drawdown", "NAN"]
          ].map(([label, value]) => (
            <Grid item xs={12} sm={6} md={3} key={label}>
              <Paper sx={{ p: 2, bgcolor: "#2d4890" }} elevation={3}>
                <Typography variant="body2" color="white">
                  {label}
                </Typography>
                <Typography variant="h6" color="white">
                  {value}
                </Typography>
              </Paper>
            </Grid>
          ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sector Allocation
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 2, color: "gray" }}>
          Portfolio Allocation by Sector
        </Typography>

        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={sectors}
              dataKey="allocation"
              nameKey="sector"
              cx="50%"
              cy="50%"
              outerRadius={130}
              label={({ name, percent }) =>
                `${name} ${(percent * 100).toFixed(1)}%`
              }
            >
              {sectors.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              wrapperStyle={{ color: "#fff" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sector Performance (%)
        </Typography>

        <Box sx={{ width: "100%", height: 500 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sectors}
              layout="vertical"
              margin={{ top: 20, right: 40, left: 100, bottom: 20 }}
            >
              <XAxis
                type="number"
                label={{
                  value: "Profit/Loss (%)",
                  position: "insideBottomRight",
                  offset: -5,
                }}
              />
              <YAxis
                type="category"
                dataKey="sector"
                tick={{ fontSize: 12 }}
                width={150}
              />
              <Tooltip
                formatter={(value) =>
                  isNaN(value) ? "NA" : `${parseFloat(value).toFixed(2)}%`
                }
              />
              <Bar dataKey="profit_loss_percent" barSize={20}>
                {sectors.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      parseFloat(entry.profit_loss_percent) > 0
                        ? "#4CAF50"
                        : parseFloat(entry.profit_loss_percent) < 0
                          ? "#F44336"
                          : "#FFC107"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ color: "#2d4890", fontWeight: 600 }}>
            Holdings
          </Typography>

          <IconButton
            onClick={handleToggleExpand}
            sx={{
              ml: 1.5,
              color: "#2d4890",
              bgcolor: expanded ? "#a8b9f5" : "transparent",
              "&:hover": {
                bgcolor: "#a8b9f5",
              },
              width: 40,
              height: 40,
              borderRadius: 2,
              transition: "background-color 0.3s ease",
            }}
            aria-label={expanded ? "Collapse holdings" : "Expand holdings"}
          >

            {expanded ? <Remove /> : <Add />}
          </IconButton>
        </Box>

        <Collapse in={true} timeout="auto" unmountOnExit>
          <Paper sx={{ bgcolor: "#2d4890" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      ["tradingsymbol", "Stock"],
                      ["sector", "Sector"],
                      ["quantity", "Quantity"],
                      ["average_price", "Avg Price"],
                      ["current_value", "Current Price"],
                      ["investment_value", "Investment"],
                      ["profit_loss", "P/L"],
                      ["profit_loss_percent", "P/L %"],
                    ].map(([key, label]) => (
                      <TableCell
                        key={key}
                        sx={{ color: "white", cursor: "pointer" }}
                        onClick={() => handleSort(key)}
                      >
                        {label}
                        {sortConfig.key === key && (
                          <span style={{ marginLeft: 6 }}>
                            {sortConfig.direction === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedHoldings.length > 0 &&
                    displayedHoldings.map((row) => (
                      <TableRow key={row.tradingsymbol}>
                        <TableCell sx={{ color: "white" }}>
                          {row.tradingsymbol || "NA"}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {row.sector || "NA"}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {isNaN(row.quantity) ? "NA" : row.quantity}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {formatValueTo2Decimal(row.average_price, "₹ ")}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {formatValueTo2Decimal(row.current_value, "₹ ")}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {formatValueTo2Decimal(row.investment_value, "₹ ")}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {formatValueTo2Decimal(row.profit_loss, "₹ ")}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {formatValueTo2Decimal(row.profit_loss_percent, "")}%
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Collapse>
      </Box>
    </>
  );
};

export default PortfolioOverview;
